/**
 * 017: structure copied from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/form-group.tsx (field sections) +
 * vendor/isomorphic/apps/isomorphic/src/app/shared/account-settings/horiozontal-block.tsx (the
 * answer rows) — both composed inside the domain CaveatBanner and WidgetCard.
 *
 * Only the data, columns and labels are ours. The screen-to-template mapping is
 * specs/017-isomorphic-theme-only/contracts/screen-map.md.
 */
import { useEffect, useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import toast from "react-hot-toast";
import { CaveatBanner, PageHeader, ErrorSurface, Field, FormGroup, WidgetCard } from "@steelyard/theme";
import HorizontalFormBlockWrapper from "@core/app/shared/account-settings/horiozontal-block";
import { can } from "../../lib/permissions";
import { getApplicability, getThresholds, setThresholds, type ApplicabilityAnswer, type Thresholds } from "../../lib/services/applicability";
import type { AccountSummary, PermissionState } from "../../types";
import { Button, Text } from "rizzui";

/**
 * Ported from apps/admin/app/(app)/settings/applicability/page.tsx. Ships unset — no defaultValue,
 * no placeholder figure, nothing a reader could mistake for the client's own number. Blank means
 * unset (nothing qualifies), never zero — I-09 owns the value.
 */
export default function ApplicabilityPage() {
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const [searchParams] = useSearchParams();
  const invoiceId = searchParams.get("invoiceId") ?? undefined;

  const [answer, setAnswer] = useState<ApplicabilityAnswer | null>(null);
  const [error, setError] = useState<unknown>(null);
  const permitted = can(permissionState, "billing.administer");

  useEffect(() => {
    if (!invoiceId || !permitted) {
      setAnswer(null);
      setError(null);
      return;
    }
    getApplicability(invoiceId).then(setAnswer, setError);
  }, [invoiceId, permitted]);

  if (!permitted) {
    return <ErrorSurface error={{ title: "Not permitted", status: 403 }} action="view the applicability configuration" />;
  }

  return (
    <div className="flex flex-col gap-6 @container">
      <PageHeader
        title="E-Invoice And E-Way Bill Applicability"
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Settings" }, { name: "E-Invoice And E-Way Bill Applicability" }]}
      />
      <Text className="mb-6 max-w-2xl text-gray-500">Which invoices attempt registration, and which consignments need an e-way bill.</Text>

      <CaveatBanner caveat="These figures must come from the client's chartered accountant.">
        <div className="flex flex-col gap-6">
          <ThresholdsForm />

          <WidgetCard title="Setting these, and what changes when you do">
            <div className="flex flex-col gap-2 pt-3 text-sm">
              <p className="text-muted-foreground">
                A change applies to invoices raised <strong>from that point on</strong>. It does not re-decide anything retrospectively: an invoice already registered keeps its IRN, and one already
                answered &ldquo;not applicable&rdquo; is not reopened by a lower threshold. If past invoices need revisiting, that is a decision someone takes invoice by invoice, not a side effect
                of editing a number here.
              </p>
            </div>
          </WidgetCard>

          {invoiceId ? (
            <WidgetCard title="What the current configuration decides for that invoice">
              {error ? (
                <div className="pt-3">
                  <ErrorSurface error={error} action="check that invoice" />
                </div>
              ) : answer ? (
                <div className="pt-3">
                  <Answer label="E-invoice registration" answer={answer.eInvoice} />
                  <Answer label="E-way bill" answer={answer.ewayBill} />
                </div>
              ) : null}
            </WidgetCard>
          ) : null}
        </div>
      </CaveatBanner>
    </div>
  );
}

const figure = z.string().trim().regex(/^(\d+(\.\d{1,2})?)?$/, "Enter an amount, or leave blank to unset.");
const states = z.string().trim().regex(/^(\d{2}(\s*,\s*\d{2})*)?$/, "Enter two-digit state codes separated by commas, or leave blank.");
const thresholdSchema = z.object({ eInvoice: figure, eway: figure, states });
type ThresholdValues = z.infer<typeof thresholdSchema>;

const toText = (v: number | null) => (v === null ? "" : String(v));
const toNumber = (v: string) => (v === "" ? null : Number(v));
const toCodes = (v: string) => v.split(",").map((c) => c.trim()).filter(Boolean);
const toForm = (t: Thresholds): ThresholdValues => ({
  eInvoice: toText(t.eInvoiceThreshold),
  eway: toText(t.ewayBillConsignmentValue),
  states: t.registeredStates.join(", "),
});

function ThresholdsForm() {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ThresholdValues>({ resolver: zodResolver(thresholdSchema), defaultValues: { eInvoice: "", eway: "", states: "" } });
  const [loadError, setLoadError] = useState<unknown>(null);
  // Save stays off until the saved values are in the form: blank means "unset", so a save before
  // they arrive would wipe the configured thresholds.
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    getThresholds().then((t) => {
      reset(toForm(t));
      setLoaded(true);
    }, setLoadError);
  }, [reset]);

  const onSubmit = handleSubmit(async (values) => {
    try {
      const t = await setThresholds({
        eInvoiceThreshold: toNumber(values.eInvoice),
        ewayBillConsignmentValue: toNumber(values.eway),
        registeredStates: toCodes(values.states),
      });
      reset(toForm(t));
      toast.success("Settings saved");
    } catch (err) {
      toast.error((err as { detail?: string; title?: string }).detail ?? (err as { title?: string }).title ?? "The settings were refused.");
    }
  });

  if (loadError) return <ErrorSurface error={loadError} action="read the thresholds" />;

  return (
    <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
      <FormGroup title="Statutory thresholds" description="Blank means unset — nothing qualifies. Zero would mean everything does.">
        <Field
          label="Turnover threshold"
          inputMode="decimal"
          error={errors.eInvoice?.message}
          hint="At or above this invoice value, an invoice attempts e-invoice registration."
          {...register("eInvoice")}
        />
        <Field
          label="Consignment value threshold"
          inputMode="decimal"
          error={errors.eway?.message}
          hint="At or above this consignment value, a movement needs an e-way bill."
          {...register("eway")}
        />
      </FormGroup>
      <FormGroup title="Registered states" description="The states the business is GST-registered in, as two-digit state codes.">
        <Field
          label="Registered states"
          error={errors.states?.message}
          hint="Comma-separated, e.g. 27, 29. Blank means unset. Held for reference; the current rules do not use it."
          {...register("states")}
        />
      </FormGroup>
      <div>
        <Button type="submit" disabled={isSubmitting || !loaded}>
          {isSubmitting ? "Saving…" : "Save thresholds"}
        </Button>
      </div>
    </form>
  );
}

function Answer({ label, answer }: { label: string; answer: { applies: boolean; reason: string } }) {
  return (
    <HorizontalFormBlockWrapper title={`${label}: ${answer.applies ? "applies" : "does not apply"}`} titleClassName="text-base font-medium">
      <Text className="text-sm text-gray-500">{answer.reason}</Text>
    </HorizontalFormBlockWrapper>
  );
}
